import { useEffect, useState } from "react";

/**
 * Whether the app is running as an installed app rather than in a browser tab.
 *
 * Two checks, because the platforms disagree. `display-mode: standalone` is the standard
 * and is what Chrome, Edge and Android answer to; `navigator.standalone` is Safari's own
 * non-standard flag and is the ONLY thing that is true for an iPhone home-screen app,
 * where the media query still reports `browser`.
 */
export function isStandalone(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;

  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    window.matchMedia?.("(display-mode: minimal-ui)").matches === true ||
    iosStandalone === true
  );
}

/** iOS and iPadOS, including an iPad reporting itself as a Mac with a touch screen. */
export function isIos(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const ua = window.navigator.userAgent;

  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1)
  );
}

/**
 * Chrome's install prompt, held back so it can be offered where it makes sense rather
 * than in whatever mini-infobar the browser chooses.
 *
 * There is no iOS equivalent: Safari has never fired `beforeinstallprompt` and installs
 * only through the Share sheet, which a page cannot open. That is why the install card
 * shows instructions on iOS and a button everywhere else — the two are genuinely
 * different, and pretending otherwise means a button that does nothing on the one
 * platform this app is most used on.
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredPrompt: InstallPromptEvent | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Held rather than shown. Without this the browser puts up its own bar, at its own
    // moment, usually over the sign-in form.
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    window.dispatchEvent(new CustomEvent("breenova:installable"));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new CustomEvent("breenova:installable"));
  });
}

export function useInstallState(): {
  standalone: boolean;
  ios: boolean;
  canPrompt: boolean;
  install: () => Promise<boolean>;
} {
  const [standalone, setStandalone] = useState(isStandalone);
  const [canPrompt, setCanPrompt] = useState(() => deferredPrompt !== null);

  useEffect(() => {
    const onInstallable = (): void => {
      setCanPrompt(deferredPrompt !== null);
      setStandalone(isStandalone());
    };

    window.addEventListener("breenova:installable", onInstallable);

    // The display mode changes under a running page when the breeder installs from the
    // browser's own menu and the app reopens in the same document.
    const query = window.matchMedia("(display-mode: standalone)");
    const onDisplayMode = (): void => setStandalone(isStandalone());
    query.addEventListener("change", onDisplayMode);

    return () => {
      window.removeEventListener("breenova:installable", onInstallable);
      query.removeEventListener("change", onDisplayMode);
    };
  }, []);

  async function install(): Promise<boolean> {
    if (!deferredPrompt) {
      return false;
    }

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    // Single-use: the browser will not accept the same event twice, and a button that
    // silently does nothing the second time is worse than one that has gone away.
    deferredPrompt = null;
    setCanPrompt(false);

    return outcome === "accepted";
  }

  return { standalone, ios: isIos(), canPrompt, install };
}

/**
 * Registers the service worker.
 *
 * Production only. In development Vite serves modules unhashed and a worker caching them
 * turns every edit into a hard-refresh hunt; the worker's own behaviour is verified
 * against a preview build instead.
 *
 * Skipped under Capacitor too. There the app is served from `capacitor://localhost` off
 * the device filesystem, so there is nothing to cache that is not already local, and the
 * origin is one service workers are not available on.
 */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  if (!import.meta.env.PROD || window.location.protocol === "capacitor:") {
    return;
  }

  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      // A registration failure is not worth a broken app. Everything the worker adds —
      // offline shell, install, push — is an improvement on a page that works without it.
    });
  });
}
